/**
 * Una tarjeta de la fila héroe de `/usage`.
 *
 * Por qué no es `KpiCard` del design system: `KpiCard` recibe un `MetricValue`
 * del catálogo, que trae `health`, `sparkline`, `benchmark` y `formula`. Un
 * `UsageKpi` no tiene ninguna de esas cuatro cosas — no hay umbral cargado para
 * "créditos consumidos", así que **no hay semáforo honesto**, y pintarle uno
 * verde por defecto sería afirmar algo que los datos no sostienen. Lo que sí
 * tiene, y `MetricValue` no, es la **base desglosada**: de qué se compone el
 * número, término por término.
 *
 * Así que esto compone las mismas primitivas que `KpiCard` (`Card`, `KpiValor`,
 * `DeltaTag`, `ProvenanceBadge`, `EmptyMetricState`) alrededor de esa diferencia.
 * No hay una primitiva nueva acá abajo.
 *
 * Dos detalles que la regla de números derivables obliga:
 *
 *  - la base va **siempre visible**, no en un tooltip ni en un drawer;
 *  - cuando la variación existe pero su porcentaje es `null` (la base era cero),
 *    se publica **el delta absoluto** con su período. `DeltaTag` devuelve `null`
 *    sin porcentaje, y perder el delta ahí escondería el único dato verdadero
 *    que había.
 */
import type { UsageKpi } from '@ops/contracts'
import { formatMetricValue, formatPeriodo } from '@ops/contracts'

import {
  Card,
  DeltaTag,
  EmptyMetricState,
  KpiValor,
  ProvenanceBadge,
} from '../../ui'
import { baseDeVariacion, formatValorDeBase } from './derivar'

export interface UsageKpiCardProps {
  kpi: UsageKpi
  /** Con esto el delta se tiñe. Sin esto va en gris: crecer no siempre es bueno. */
  direccion?: 'higher_better' | 'lower_better'
  /** Ausente = el rol no puede hacer nada con un número que falta. */
  onPedirDato?: () => void
  now: Date
}

export function UsageKpiCard({
  kpi,
  direccion,
  onPedirDato,
  now,
}: UsageKpiCardProps) {
  const variacion = kpi.variacion
  const soloDelta = variacion !== null && variacion.pct === null

  return (
    <Card
      as="section"
      ariaLabel={kpi.nombre}
      padding="sm"
      className="flex flex-col gap-2"
    >
      <h3 className="rotulo min-w-0 truncate">{kpi.nombre}</h3>

      {kpi.valor === null ? (
        <EmptyMetricState
          metricNombre={kpi.nombre}
          size="md"
          detalle="Lo mide Langfuse. Sin la corrida del adapter el período queda sin número, que no es lo mismo que un período en cero."
          textoAccion="Pedir la carga"
          {...(onPedirDato === undefined ? {} : { onLoadData: onPedirDato })}
        />
      ) : (
        <KpiValor valor={kpi.valor} unidad={kpi.unidad} />
      )}

      {variacion !== null && !soloDelta ? (
        <DeltaTag
          deltaPct={variacion.pct}
          base={baseDeVariacion(variacion)}
          unidad={kpi.unidad}
          {...(direccion === undefined ? {} : { direccion })}
          size="sm"
        />
      ) : null}

      {soloDelta && variacion !== null ? (
        /* Sin porcentaje pero con delta: el número absoluto sigue siendo cierto
           y su base era cero, cosa que se dice con todas las letras. */
        <p data-testid="usage-kpi-delta-absoluto" className="text-micro text-muted-strong">
          <b className="tabular font-semibold text-text">
            {variacion.delta > 0 ? '+' : ''}
            {formatMetricValue(variacion.delta, kpi.unidad)}
          </b>{' '}
          contra {formatPeriodo(variacion.base.periodo)} (
          {formatMetricValue(variacion.base.valor, kpi.unidad)}): sin base no hay
          porcentaje que calcular.
        </p>
      ) : null}

      {kpi.base.length > 0 ? (
        <dl
          data-testid="usage-kpi-base"
          className="m-0 flex flex-wrap gap-x-3 gap-y-0.5 text-micro text-muted-strong"
        >
          {kpi.base.map((termino) => (
            <div key={termino.etiqueta} className="flex items-baseline gap-1">
              <dt>{termino.etiqueta}</dt>
              <dd className="tabular m-0 font-semibold text-text">
                {formatValorDeBase(termino.valor, termino.unidad) ?? 'sin dato'}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      <footer className="mt-auto pt-1">
        <ProvenanceBadge provenance={kpi.provenance} now={now} />
      </footer>
    </Card>
  )
}
